import React, { useState, useEffect } from 'react';
import { Button } from '../../../ui/button';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../../../ui/alert-dialog';
import {
    Loader2,
    AlertTriangle,
    CheckCircle,
    Copy,
    Download,
    Printer,
    Save,
    LucideIcon,
} from 'lucide-react';
import { ActionNode } from '../../types';
import { cn } from '../../../../lib/utils';

export interface ValidationResult {
    isValid: boolean;
    missingFields: { id: string; title: string }[];
}

interface DocumentButtonProps {
    field: ActionNode;
    onClick: () => Promise<void> | void;
    disabled?: boolean;
    loading?: boolean;
    validate?: () => ValidationResult;
}

type ButtonVariant =
    | 'default'
    | 'secondary'
    | 'destructive'
    | 'outline'
    | 'ghost'
    | 'link';

const ICON_BY_NAME: Record<string, LucideIcon> = {
    copy: Copy,
    download: Download,
    save: Save,
    complete: CheckCircle,
    preview: Printer,
    check: CheckCircle,
    'check-circle': CheckCircle,
    warning: AlertTriangle,
    alert: AlertTriangle,
};

const VARIANT_BY_COLOR: Record<string, ButtonVariant> = {
    default: 'default',
    secondary: 'secondary',
    destructive: 'destructive',
    outline: 'outline',
    ghost: 'ghost',
    link: 'link',
    success: 'default',
    warning: 'outline',
    neutral: 'secondary',
};

export function DocumentButton({
    field,
    onClick,
    disabled = false,
    loading = false,
    validate,
}: DocumentButtonProps) {
    const [showConfirm, setShowConfirm] = useState(false);
    const [showValidationError, setShowValidationError] = useState(false);
    const [validationErrors, setValidationErrors] = useState<{ id: string; title: string }[]>([]);
    const [isSuccess, setIsSuccess] = useState(false);

    useEffect(() => {
        if (isSuccess) {
            const timer = setTimeout(() => {
                setIsSuccess(false);
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, [isSuccess]);

    const handleClick = async () => {
        // Run validation first if provided
        if (validate) {
            const result = validate();
            if (!result.isValid) {
                setValidationErrors(result.missingFields);
                setShowValidationError(true);
                return;
            }
        }

        if (field.confirmation) {
            setShowConfirm(true);
        } else {
            await executeClick();
        }
    };

    const handleConfirm = async () => {
        setShowConfirm(false);
        await executeClick();
    };

    const executeClick = async () => {
        await onClick();
        if (field.successMessage) {
            setIsSuccess(true);
        }
    };

    const colorKey = isSuccess ? 'success' : field.style?.color;
    const variant = colorKey
        ? VARIANT_BY_COLOR[colorKey] || 'default'
        : 'default';
    const iconKey = isSuccess ? 'complete' : field.style?.icon;
    const Icon = iconKey ? ICON_BY_NAME[iconKey] || null : null;

    const title =
        isSuccess && field.successMessage ? field.successMessage : field.title;
    const isInline = field.style?.inline || field.style?.fullWidth === false;
    const buttonSize = field.style?.size || 'default';

    return (
        <>
            <Button
                onClick={handleClick}
                disabled={disabled || loading}
                type="button"
                className={cn(
                    isInline ? 'justify-start' : 'w-full justify-center',
                    field.style?.className
                )}
                variant={variant}
                size={buttonSize}
            >
                {loading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                    Icon && <Icon className="mr-2 h-4 w-4" />
                )}
                {title}
            </Button>

            {field.confirmation && (
                <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>
                                {field.confirmation.title}
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                                {field.confirmation.message}
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={handleConfirm}>
                                {field.confirmation.confirmText || 'Confirm'}
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            )}

            <AlertDialog open={showValidationError} onOpenChange={setShowValidationError}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <AlertTriangle className="h-5 w-5 text-amber-500" />
                            Required Fields Missing
                        </AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div>
                                <p className="mb-3">
                                    Please fill in the following required fields before completing the report:
                                </p>
                                <ul className="list-disc pl-5 space-y-1">
                                    {validationErrors.map((field) => (
                                        <li key={field.id} className="text-foreground font-medium">
                                            {field.title}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogAction onClick={() => setShowValidationError(false)}>
                            OK
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
